/* eslint-disable @typescript-eslint/ban-ts-comment */
import type {
  ActionCreatorWithPayload,
  ActionCreatorWithoutPayload,
} from "@reduxjs/toolkit";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import ErrorComponent from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading/loading";
import __ from "web/Layout/Translations";

import ArrowLeftIcon from "web/assets/icons/arrow-left.svg";
import ArrowRightIcon from "web/assets/icons/arrow-right.svg";

import useSubscriptionsView from "web/hooks/useSubscriptionsView";

import jsonParse from "web/utils/data/parser/string/jsonParse";
import getSubscriptionChoiceUrl from "web/utils/page/product/subscription/getSubscriptionChoiceUrl";

import urls from "web/constants/urls";

import type { ICategory } from "web/types/Category";
import type { ICategoryCount } from "web/types/CategoryCount";
import { Functionality } from "web/types/Employee";
import {
  ICategoryFunctionalityMenuItem,
  IFunctionalityMenuItem,
  IUrlFunctionalityMenuItem,
  IUrlMenuItem,
  MenuItemType,
} from "web/types/Menu";
import type { Nullable } from "web/types/Utils";

import { useGetMenuWebviewQuery } from "web/features/api/graphQLApiSlice";
import { useGetCustomerDetailsQuery } from "web/features/customer/customerApiSlice";
import { useGetEmployeeDetailsQuery } from "web/features/employee/employeeApiSlice";

import classes from "./list.scss";
import NavItem from "./navItem";

const getLink = (path: string) => (!path.indexOf("/") ? path : `/${path}`);

const redirectToCategory = (categoryObj: ICategory) => {
  switch (true) {
    case categoryObj.url_key === "turystyka-online":
      return getLink("tourism");
    default:
      return getLink(categoryObj.request_path);
  }
};

interface INavigationWebViewListProps {
  rootCategoryId: number;
  startCategoryId: Nullable<number>;
  categoriesTree?: string[];
  categoriesData: ICategory[];
  categoriesSizes: ICategoryCount[];
  setCategory: ActionCreatorWithPayload<Nullable<number>, string>;
  closeNavigationAction: ActionCreatorWithoutPayload<string>;
}

interface ISubmenuItem {
  title: string;
  url: string;
}

interface ISubmenu {
  title: string;
  items: ISubmenuItem[];
}

const NavigationWebViewList: FC<INavigationWebViewListProps> = ({
  rootCategoryId,
  startCategoryId = "",
  categoriesTree,
  categoriesData,
  categoriesSizes,
  setCategory,
  closeNavigationAction,
}) => {
  const {
    app: { storeConfig },
  } = useSelector((state) => state);

  const [submenu, setSubmenu] = useState<Nullable<ISubmenu>>(null);
  const { hasEnabledAnySubscriptions, config: subscriptionConfig } =
    useSelector((state) => state.subscriptionQuote);

  const { enableMobilePreview, enableNewSubscriptionUI } =
    useSubscriptionsView();
  const {
    mybenefit_active_url: myBenefitActiveUrlPl,
    mybenefit_active_url_en: myBenefitActiveUrlEn,
    mybenefit_active_menu: myBenefitActiveMenuPl,
    mybenefit_active_menu_en: myBenefitActiveMenuEn,
    msi_url: msiUrlPl,
    msi_url_en: msiUrlEn,
    msi_menu_en: msiMenuEn,
  } = storeConfig;
  const { data: employee } = useGetEmployeeDetailsQuery();
  const {
    functionalities,
    msiCartsEnabled: msiCartsEnabledSelector,
    mbActiveEnabled: mbActiveEnabledSelector,
  } = employee || {};

  const { data, isLoading, isError } = useGetMenuWebviewQuery();
  const { data: customer } = useGetCustomerDetailsQuery();
  const { language } = customer || {};

  const isEn = language === "en_GB";
  const myBenefitActiveUrl = isEn ? myBenefitActiveUrlEn : myBenefitActiveUrlPl;
  const myBenefitActiveMenu = isEn
    ? myBenefitActiveMenuEn
    : myBenefitActiveMenuPl;

  const msiUrl = isEn ? msiUrlEn : msiUrlPl;

  const dispatch = useDispatch();

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorComponent />;
  }

  const initialStartCategoryId = startCategoryId === 2 ? null : startCategoryId;
  const treeCurrent = categoriesTree?.reduce<ICategory[]>((result, path) => {
    switch (true) {
      case !!initialStartCategoryId: {
        const startCategoryIdStringBegin = `${startCategoryId}/`;
        const startCategoryIdStringMiddle = `/${startCategoryId}/`;

        let pathSliced: string;
        if (path.indexOf(startCategoryIdStringBegin) === 0) {
          pathSliced = path.slice(startCategoryIdStringBegin.length);
        }

        if (path.indexOf(startCategoryIdStringMiddle) !== -1) {
          pathSliced = path.slice(
            path.indexOf(startCategoryIdStringMiddle) +
              startCategoryIdStringMiddle.length
          );
        }

        // @ts-ignore
        if (pathSliced?.length) {
          const idChild = parseInt(pathSliced, 10);
          const categoryDataChild =
            idChild &&
            categoriesData.find(
              (category) => category && +category.id === +idChild
            );
          return categoryDataChild ? [...result, categoryDataChild] : result;
        }

        return result;
      }
      case path.indexOf("/") === -1: {
        const categoryData = categoriesData.find(
          (category) => category && +category.id === +path
        );
        return categoryData ? [...result, categoryData] : result;
      }
      default: {
        return result;
      }
    }
  }, []);

  const treeCurrentUnique = [
    ...new Map(treeCurrent?.map((item) => [item.id, item])).values(),
  ];
  const startCategoryData = initialStartCategoryId
    ? // @ts-ignore
      categoriesData.find((category) => +category.id === +startCategoryId)
    : null;

  const list = treeCurrentUnique
    .sort((a, b) => a?.position - b?.position)
    .map((category) => {
      const categoryIdStringBegin = `${category.id}/`;
      const categoryIdStringMiddle = `/${category.id}/`;
      const hasSubcategory = categoriesTree?.some((categoryPath) => {
        switch (true) {
          case categoryPath.indexOf(categoryIdStringBegin) === 0:
          case categoryPath.indexOf(categoryIdStringMiddle) !== -1: {
            return true;
          }
          default: {
            return false;
          }
        }
      });

      const content = (
        <>
          <span>{category.name}</span>
          <ArrowRightIcon width={10} />
        </>
      );

      if (categoriesSizes.find((x) => +x.id === +category.id)) {
        if (hasSubcategory) {
          return (
            <button
              key={category.id}
              type="button"
              className={classes.navItem}
              onClick={() => dispatch(setCategory(category.id))}
            >
              {content}
            </button>
          );
        }
        return (
          <Link
            key={category.id}
            className={classes.navItem}
            to={redirectToCategory(category)}
            onClick={() => {
              dispatch(setCategory(null));
              dispatch(closeNavigationAction());
            }}
          >
            {content}
          </Link>
        );
      }
      return null;
    });

  const menuData = jsonParse<
    (
      | IFunctionalityMenuItem
      | IUrlMenuItem
      | IUrlFunctionalityMenuItem
      | ICategoryFunctionalityMenuItem
    )[]
  >(data?.menu, true)?.filter(({ show_on_mobile }) => show_on_mobile === "1");
  const additionalList = menuData.map((item) => {
    const {
      type,
      title,
      code,
      // @ts-ignore
      url,
      // @ts-ignore
      url_en,
      // @ts-ignore
      functionality,
      // @ts-ignore
      url_functionality,
      // @ts-ignore
      category_functionality,
    } = item;

    if (type === MenuItemType.URL) {
      return <NavItem key={code} to={isEn ? url_en : url} title={title} />;
    }

    const funcFromEmployee = functionalities?.find(
      (func) => func === functionality || func === url_functionality
    );

    if (type === MenuItemType.FUNCTIONALITY) {
      if (
        funcFromEmployee === Functionality.ENABLE_MYBENEFIT_ACTIVE &&
        mbActiveEnabledSelector &&
        myBenefitActiveUrl
      ) {
        return (
          <NavItem
            key={code}
            to={myBenefitActiveUrl}
            title={myBenefitActiveMenu ?? title}
          />
        );
      }

      if (funcFromEmployee === Functionality.ENABLE_MSI_CARDS) {
        return (
          <NavItem key={code} to={msiUrl} title={isEn ? msiMenuEn : title} />
        );
      }
    }

    if (type === MenuItemType.URL_FUNCTIONALITY) {
      if (
        funcFromEmployee &&
        (funcFromEmployee !== Functionality.ENABLE_MSI_CARDS ||
          (funcFromEmployee === Functionality.ENABLE_MSI_CARDS &&
            msiCartsEnabledSelector))
      ) {
        return <NavItem key={code} to={isEn ? url_en : url} title={title} />;
      }
    }
    if (type === MenuItemType.CATEGORY_FUNCTIONALITY) {
      if (
        category_functionality === Functionality.SUBSCRIPTIONS &&
        hasEnabledAnySubscriptions &&
        (enableMobilePreview || enableNewSubscriptionUI)
      ) {
        return (
          <button
            type="button"
            key={code}
            className={classes.navItem}
            onClick={() =>
              setSubmenu({
                title,
                items: [
                  ...(enableNewSubscriptionUI
                    ? [
                        {
                          title: __("Wybierz abonamenty"),
                          url: getSubscriptionChoiceUrl(
                            subscriptionConfig.configuratorWindowOpen,
                            subscriptionConfig.configuratorWindowOpenUms
                          ),
                        },
                      ]
                    : []),
                  {
                    title: __("Moje abonamenty"),
                    url: urls.customerSubscriptions,
                  },
                  ...(enableNewSubscriptionUI
                    ? [
                        {
                          title: __("Katalog abonamentów"),
                          url: "/abonamenty.html",
                        },
                      ]
                    : []),
                ],
              })
            }
          >
            <span>{__(title)}</span>
            <ArrowRightIcon width={10} />
          </button>
        );
      }
    }

    return null;
  });

  if (!startCategoryData) {
    list?.push(...additionalList);
  }

  if (submenu) {
    return (
      <div className={classes.subcategoryRoot}>
        <div className={classes.subcategory}>
          <button
            className={classes.subcategoryHeader}
            type="button"
            onClick={() => {
              setSubmenu(null);
            }}
          >
            <ArrowLeftIcon width={10} />
            <span>{__(submenu.title)}</span>
          </button>
          {submenu.items?.map((submenuItem) => (
            <Link
              key={submenuItem.url}
              className={classes.navItem}
              to={submenuItem.url}
              onClick={() => {
                dispatch(setCategory(null));
                dispatch(closeNavigationAction());
              }}
            >
              {submenuItem.title}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return startCategoryData ? (
    <div className={classes.subcategoryRoot}>
      <div className={classes.subcategory}>
        <button
          className={classes.subcategoryHeader}
          type="button"
          onClick={() => {
            const pathSplit = startCategoryData.path.split("/");
            const currentIdIndex = pathSplit.indexOf(`${startCategoryData.id}`);
            const parentId = currentIdIndex
              ? +pathSplit[currentIdIndex - 1]
              : null;
            const parentIdProcessed =
              parentId && parentId !== rootCategoryId ? parentId : null;
            dispatch(setCategory(parentIdProcessed));
          }}
        >
          <ArrowLeftIcon width={10} />
          <span>{startCategoryData.name}</span>
        </button>
        <Link
          className={classes.navItem}
          to={getLink(startCategoryData.request_path)}
          onClick={() => {
            dispatch(setCategory(null));
            dispatch(closeNavigationAction());
          }}
        >
          <span>{__("Wszystkie w ") + startCategoryData.name}</span>
          <ArrowRightIcon width={10} />
        </Link>
        {list}
      </div>
    </div>
  ) : (
    <div className={classes.categoryRoot}>{list}</div>
  );
};

export default NavigationWebViewList;
